import { create } from "zustand";
import {
  EngineDriver,
  type CommandResult,
  type GridPos,
  type GridSnapshot,
  type NodeState,
  type PollutionReason,
  type Rotation,
  type PrecursorTier,
  type ResaleRule,
  type SimEvent,
  type StageDef,
  type ValveState,
} from "@reagent/sim";
import { CARDS, PLAYABLE_STAGES, TUTORIAL_STAGE } from "@reagent/content";
import { currentStage, runStages, stageSeed } from "@reagent/meta";
import { useRunStore } from "./runStore";
import { isTutorialDone } from "./tutorialState";

/**
 * Composition root for the sim. The driver is constructed here and nowhere
 * else, which is what made M3 a one-line swap: everything downstream knows
 * only GridSnapshot and SimEvent.
 *
 * Stage and cards come from @reagent/content; which stage, and with which deck
 * and upgrades, comes from the run in @reagent/meta. The engine ships no
 * content of its own, so there is no default to fall back to and no way for a
 * test fixture to reach the player.
 */

/**
 * The fixed cycle a flowMover's valve steps through on each toggle — both the
 * keyboard and the board-click affordance use this, so the two inputs can
 * never disagree about what "next" means. `null` and `open` both step to
 * `closed` (spec 0024: they're distinct engine states, but there's only one
 * "open" rung on this ladder). `route` steps through `outgoingEdgeIds` in the
 * order given, then wraps to `open`. An empty `outgoingEdgeIds` skips `route`
 * entirely (nothing to route to) and cycles open <-> closed only.
 */
export function nextValveState(
  current: ValveState | null,
  outgoingEdgeIds: readonly string[],
): ValveState {
  if (current === null || current.kind === "open") {
    return { kind: "closed" };
  }
  if (current.kind === "closed") {
    if (outgoingEdgeIds.length === 0) {
      return { kind: "open" };
    }
    // Just checked outgoingEdgeIds.length > 0 above.
    return { kind: "route", edgeId: outgoingEdgeIds[0]! };
  }
  // current.kind === "route"
  const currentIndex = outgoingEdgeIds.indexOf(current.edgeId);
  const nextIndex = (currentIndex + 1) % outgoingEdgeIds.length;
  if (nextIndex === 0) {
    // Wrapped around; go to open
    return { kind: "open" };
  }
  // nextIndex is always < outgoingEdgeIds.length by construction.
  return { kind: "route", edgeId: outgoingEdgeIds[nextIndex]! };
}

/**
 * Free play is the balance-pass mode: any stage, any seed, the stage's own
 * deck. Run mode is the game — the stage, the deck and the rig upgrades are
 * all the run's.
 */
export type StageMode = "run" | "free";

let mode: StageMode = "run";
let stage: StageDef = TUTORIAL_STAGE;
let seed = 1;

/** The stage and seed the current run is up to. */
function runStage(): { stage: StageDef; seed: number } {
  const run = useRunStore.getState().run;
  return {
    // A finished run has no "current" stage, and a picking run has none yet;
    // fall back so the board still draws something behind the end-of-run and
    // pathway-pick screens. The induction is outside every run (spec 0037).
    stage: currentStage(run) ?? TUTORIAL_STAGE,
    seed: stageSeed(run.seed, run.stageIndex),
  };
}

/**
 * A run nobody has touched yet. Offering the induction over a shift already
 * two batches in would be worse than not offering it at all.
 */
function isUntouchedRun(): boolean {
  const run = useRunStore.getState().run;
  return run.stageIndex === 0 && run.results.length === 0 && run.choices.length === 0;
}

// A first-time player opens on the induction rather than on batch one. It is
// free play, which is exactly right: the induction is not part of a shift and
// failing it costs nothing (M6).
if (!isTutorialDone() && isUntouchedRun()) {
  mode = "free";
  stage = TUTORIAL_STAGE;
  seed = 1;
} else {
  ({ stage, seed } = runStage());
}

let driver = new EngineDriver({ stage, cards: CARDS, seed });

type Listener = (event: SimEvent) => void;
const listeners = new Set<Listener>();

/** One player-installed node or pipe, keyed by its `nodeId ?? edgeId`. */
export interface InstalledUnit {
  readonly cardId: string;
  readonly instanceId: string;
  readonly costCredits: number;
}

/**
 * Player-installed hardware, folded from `hardware.installed`/
 * `hardware.dismantled` events (spec 0021 T4) — `GridSnapshot` carries neither
 * `costCredits` nor which nodes/edges are player-bought, so this is tracked
 * client-side rather than reached through the protocol. Reset whenever
 * `rebuild()` points the fan-out at a fresh driver, which starts with nothing
 * installed.
 */
let installedUnits: Record<string, InstalledUnit> = {};

/**
 * Points the fan-out at a driver. Switching stages builds a new one, and the
 * FX layer's subscription has to survive that — so subscribers attach to this
 * module, never to a driver instance.
 */
function attach(next: EngineDriver): void {
  next.subscribe((event) => {
    if (event.type === "hardware.installed") {
      const key = event.nodeId ?? event.edgeId!;
      installedUnits = {
        ...installedUnits,
        [key]: { cardId: event.cardId, instanceId: event.instanceId, costCredits: event.costCredits },
      };
    } else if (event.type === "hardware.moved") {
      // A moved unit keeps its entry (same id, same purchase). Pipes sold back
      // with a moved node arrive as hardware.dismantled and leave below.
    } else if (event.type === "hardware.dismantled") {
      const key = event.nodeId ?? event.edgeId!;
      if (key in installedUnits) {
        installedUnits = Object.fromEntries(
          Object.entries(installedUnits).filter(([k]) => k !== key),
        );
      }
    }
    for (const fn of [...listeners]) fn(event);
  });
}

attach(driver);

/**
 * The FX layer's event feed. It fans in both real driver events and the
 * fabricated ones the debug panel fires, because the panel needs to show an
 * explosion that never happened and SimDriver rightly has no method for
 * lying. Everything reaching the renderer still comes through this one pipe,
 * so a hand-fired effect looks exactly as it will in a real run.
 */
export function subscribeEvents(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export type RunSpeed = "paused" | "slow" | "normal" | "fast";

export const SPEED_SECONDS_PER_TICK: Record<RunSpeed, number> = {
  paused: Infinity,
  slow: 1.1,
  normal: 0.62,
  fast: 0.3,
};

/** A pipe card waiting for its second endpoint. */
export interface PendingPipe {
  readonly instanceId: string;
  readonly fromNodeId: string;
}

/**
 * A Setup-catalogue selection awaiting a tile (mirrors `PendingPipe`). A pipe
 * offer takes two taps, one per machine it joins; `fromNodeId` holds the
 * first, and is always `null` for a machine offer.
 */
export interface PendingInstall {
  readonly cardId: string;
  readonly fromNodeId: string | null;
  /**
   * Spec 0068: the recipes the player picked for a machine card, sent with
   * `installHardware`. Absent when the pre-selected option stood — the engine
   * then binds the card's own recipe, or leaves the node unbound.
   */
  readonly recipeIds?: readonly string[];
}

/**
 * A live-build move awaiting its destination (spec 0067). A node takes one tap
 * on a tile; a pipe takes two taps, one per machine it will join, with
 * `fromNodeId` holding the first. `soldBackEdgeIds` is what T9 marks and T8
 * prices: pipes do not follow a moved node (OQ4), so every pipe touching it is
 * sold back at cost. Empty for a pipe move.
 */
export type PendingMove =
  | { readonly kind: "node"; readonly nodeId: string; readonly soldBackEdgeIds: readonly string[] }
  | { readonly kind: "pipe"; readonly edgeId: string; readonly fromNodeId: string | null };

/** Every pipe touching the node, in snapshot (creation) order — what a move sells back. */
export function pipesSoldBackByMove(snapshot: GridSnapshot, nodeId: string): string[] {
  return snapshot.edges
    .filter((e) => e.fromNodeId === nodeId || e.toNodeId === nodeId)
    .map((e) => e.id);
}

/** True where the catalogue tray and inspector actions are live: Setup, or Play under a `liveBuild` rule. */
export function canBuildNow(snapshot: GridSnapshot): boolean {
  return snapshot.phase === "setup" || (snapshot.phase === "play" && snapshot.liveBuild !== null);
}

interface GameState {
  snapshot: GridSnapshot;
  speed: RunSpeed;
  /** Why the last command was refused, surfaced as a toast then cleared. */
  rejection: string | null;
  pendingPipe: PendingPipe | null;
  pendingInstall: PendingInstall | null;
  /** Spec 0067: the move waiting for a destination, or null. */
  pendingMove: PendingMove | null;

  /** Which stage is loaded. */
  stageId: string;
  /**
   * The current stage's depreciation ladder (spec 0021), for the dismantle
   * refund preview — `undefined` on a standalone stage with no rule, in which
   * case `resaleRefund`'s own fallback (full `costCredits`) applies.
   */
  resaleRule: ResaleRule | undefined;
  /**
   * Player-installed hardware, keyed by `nodeId ?? edgeId` — folded from
   * `hardware.installed`/`hardware.dismantled` events, since `GridSnapshot`
   * carries neither which units are player-bought nor their `costCredits`
   * (spec 0021 T4).
   */
  installedUnits: Record<string, InstalledUnit>;
  /** The three batches a shift is made of (empty until the pick) — what the run's progress pips count. */
  stages: readonly StageDef[];
  /** Everything loadable by hand, induction included. Never a run's business. */
  pickable: readonly StageDef[];
  mode: StageMode;
  /**
   * Loads a stage from the start, replacing the driver. Selecting a stage by
   * hand drops out of run mode: an explicit seed reproduces a deal exactly,
   * which is a balance-pass tool, not a way to re-roll a run.
   */
  selectStage: (stageId: string, seed?: number) => void;
  /** (Re)loads whatever stage the run is up to, with its deck and upgrades. */
  loadRunStage: () => void;
  /** Notices worth reading but not worth a modal — deck pollution, mostly. */
  notice: string | null;
  clearNotice: () => void;

  playCardAt: (instanceId: string, at: GridPos) => void;
  playInstant: (instanceId: string) => void;

  beginPipe: (instanceId: string, fromNodeId: string) => void;
  completePipe: (toNodeId: string) => void;
  cancelPipe: () => void;

  /** Toggles a Setup-catalogue offer as the target of the next tile click. */
  selectSetupOffer: (cardId: string | null, recipeIds?: readonly string[]) => void;
  /**
   * Buys and places a catalogue offer at a tile in one step (Setup, or Play under
   * a liveBuild rule). `recipeIds` is sent only when given (spec 0068).
   */
  installHardwareAt: (cardId: string, at: GridPos, recipeIds?: readonly string[]) => void;
  /** Buys a facility mitigation (Phase 1 only, occupies no tile). */
  installFacilityMitigation: (cardId: string) => void;
  /** Reverses a player-installed node, never a stage-authored one (Setup only). */
  dismantleHardwareAt: (nodeId: string) => void;
  /** Chooses a Budget or Premium sourcing tier for an inputTank (Setup only). */
  setPrecursor: (nodeId: string, tier: PrecursorTier) => void;
  /** Sets the valve routing on a flow mover during Play. */
  setValveAt: (nodeId: string, state: ValveState) => void;
  /** Reverses a player-installed pipe, never a stage-authored one (Setup only). */
  dismantlePipeAt: (edgeId: string) => void;
  /** Whatever bought hardware is on this tile — a machine first, else a pipe crossing it. */
  dismantleAt: (at: GridPos) => void;
  /** Picks a player-installed machine to move; its sold-back pipes are on `pendingMove`. */
  beginMoveNode: (nodeId: string) => void;
  /** Picks a player-installed pipe to re-attach (two taps, one per machine). */
  beginMovePipe: (edgeId: string) => void;
  cancelMove: () => void;
  /** Issues `moveHardware` for the pending node move. */
  moveNodeTo: (at: GridPos, rotation?: Rotation) => void;
  /** Issues `moveHardware` for the pending pipe move. */
  movePipeTo: (fromNodeId: string, toNodeId: string) => void;
  /** Issues `cleanTile` for a contaminated tile. */
  cleanTileAt: (at: GridPos) => void;
  /** Issues `bindRecipe` (spec 0068): replaces a placed machine's recipes. Free in Setup, labour in Play. */
  rebindRecipeAt: (nodeId: string, recipeIds: readonly string[]) => void;
  /** Leaves Setup once the player is done shopping (setup -> draw -> play). */
  leaveSetup: () => void;
  /** Leaves Status once the player has read the turn's outcome (status -> draw -> play). */
  advanceFromStatus: () => void;

  /** Board click, routed here so tile picking has one consumer, not many. */
  clickTile: (at: GridPos) => void;

  /** Ends the Play phase and starts the tick clock. */
  startRun: () => void;
  /** Advances one tick, rolling the turn over when the Run phase is spent. */
  tick: () => SimEvent[];
  setSpeed: (speed: RunSpeed) => void;

  reject: (reason: string) => void;
  clearRejection: () => void;

  fireDebugEvent: (event: SimEvent) => void;
  reset: () => void;
}

/**
 * The engine opens in Setup, then Draw, with the player's turn actually
 * beginning in Play — so step across both on startup and after a restart,
 * but only when Setup is genuinely nothing to decide.
 *
 * Every stage shipped today has an empty catalogue and a zero budget, so
 * forcing a player through a visible, empty Setup screen on every single
 * stage load would be pure friction with nothing to decide — the same
 * reasoning that already skips Draw, which deals a hand nobody chooses. A
 * stage that ships a real catalogue (spec 0037) stops in Setup instead, and
 * the SetupScreen takes over.
 */
function enterPlay(): void {
  const snap = driver.getSnapshot();
  // An empty catalogue alone decides it: in a run, `composeStage` hands every
  // stage the player's whole Credit balance, and money with nothing to buy is
  // still nothing to decide.
  if (snap.phase === "setup" && snap.setupCatalogue.length === 0) {
    driver.applyCommand({ type: "advancePhase" }); // setup -> draw
    driver.applyCommand({ type: "advancePhase" }); // draw -> play
    return;
  }
  if (snap.phase === "draw") {
    driver.applyCommand({ type: "advancePhase" });
  }
}

enterPlay();

/**
 * Escape hatch onto the module's own driver, for tests only — mirrors
 * `EngineDriver.internals`'s own comment, one level up: `driver` here is a
 * private module variable rather than a class field, so a test that needs to
 * stack the hand (a real `isPlayable: false` hazard card, say) has nothing
 * else to reach through. Not for the renderer: everything the UI needs is
 * already on `GridSnapshot`.
 */
export function __testDriverInternals() {
  return driver.internals;
}

export function nodeAt(snapshot: GridSnapshot, at: GridPos): NodeState | null {
  return (
    snapshot.nodes.find((n) => !n.isDestroyed && n.pos.x === at.x && n.pos.y === at.y) ?? null
  );
}

/** Builds a driver for the current stage/seed and points the FX feed at it. */
function rebuild(): void {
  // A fresh driver starts with nothing installed.
  installedUnits = {};
  driver = new EngineDriver({ stage, cards: CARDS, seed });
  attach(driver);
  enterPlay();
}

/** The board state that always accompanies loading a stage. */
function freshBoard() {
  return {
    snapshot: driver.getSnapshot(),
    speed: "paused" as RunSpeed,
    rejection: null,
    pendingPipe: null,
    pendingInstall: null,
    pendingMove: null,
    notice: null,
    installedUnits,
    resaleRule: stage.resaleRule,
  };
}

export const useGameStore = create<GameState>((set, get) => {
  /** Runs a command and turns the engine's refusal, if any, into a toast. */
  const commit = (run: () => CommandResult) => {
    const result = run();
    const rejection = result.ok ? null : result.rejection.message;
    set({ snapshot: driver.getSnapshot(), rejection, installedUnits });
  };

  return {
    snapshot: driver.getSnapshot(),
    speed: "paused",
    rejection: null,
    pendingPipe: null,
    pendingInstall: null,
    pendingMove: null,
    stageId: stage.id,
    resaleRule: stage.resaleRule,
    installedUnits,
    stages: runStages(useRunStore.getState().run),
    pickable: PLAYABLE_STAGES,
    mode,
    notice: null,

    clearNotice: () => set({ notice: null }),

    selectStage: (nextStageId, nextSeed) => {
      const next = PLAYABLE_STAGES.find((s) => s.id === nextStageId);
      if (!next) return;
      mode = "free";
      stage = next;
      if (nextSeed !== undefined) seed = nextSeed;
      rebuild();
      set({ mode, stageId: stage.id, ...freshBoard() });
    },

    loadRunStage: () => {
      mode = "run";
      ({ stage, seed } = runStage());
      rebuild();
      // The pick fixes the shift's stages, so they are read again on every load.
      set({ mode, stageId: stage.id, stages: runStages(useRunStore.getState().run), ...freshBoard() });
    },

    playCardAt: (instanceId, at) =>
      commit(() => driver.applyCommand({ type: "playCard", instanceId, at })),

    playInstant: (instanceId) =>
      commit(() => driver.applyCommand({ type: "playCard", instanceId, at: null })),

    beginPipe: (instanceId, fromNodeId) =>
      set({ pendingPipe: { instanceId, fromNodeId }, rejection: null }),

    completePipe: (toNodeId) => {
      const pending = get().pendingPipe;
      if (!pending) return;
      set({ pendingPipe: null });
      commit(() =>
        driver.applyCommand({
          type: "connectPipe",
          instanceId: pending.instanceId,
          fromNodeId: pending.fromNodeId,
          toNodeId,
        }),
      );
    },

    cancelPipe: () => set({ pendingPipe: null }),

    selectSetupOffer: (cardId, recipeIds) =>
      set({
        pendingInstall: cardId
          ? { cardId, fromNodeId: null, ...(recipeIds === undefined ? {} : { recipeIds }) }
          : null,
      }),

    installHardwareAt: (cardId, at, recipeIds) => {
      commit(() =>
        driver.applyCommand({
          type: "installHardware",
          cardId,
          at,
          ...(recipeIds === undefined ? {} : { recipeIds }),
        }),
      );
      // Cleared whether the install landed or was refused — a refusal already
      // surfaces via commit's rejection toast, same as every other command here.
      set({ pendingInstall: null });
    },

    installFacilityMitigation: (cardId) => {
      commit(() => driver.applyCommand({ type: "installHardware", cardId, at: null }));
    },

    dismantleHardwareAt: (nodeId) =>
      commit(() => driver.applyCommand({ type: "dismantleHardware", nodeId })),

    setPrecursor: (nodeId, tier) =>
      commit(() => driver.applyCommand({ type: "setPrecursor", nodeId, tier })),

    setValveAt: (nodeId, state) =>
      commit(() => driver.applyCommand({ type: "setValve", nodeId, state })),

    dismantlePipeAt: (edgeId) =>
      commit(() => driver.applyCommand({ type: "dismantleHardware", edgeId })),

    dismantleAt: (at) => {
      const { snapshot } = get();
      const node = nodeAt(snapshot, at);
      if (node) {
        get().dismantleHardwareAt(node.id);
        return;
      }
      // Latest-laid first: a bought pipe is appended after the stage's own,
      // so where two share a tile the one the player can take back wins.
      const edge = [...snapshot.edges]
        .reverse()
        .find((e) => !e.isBurst && e.path.some((p) => p.x === at.x && p.y === at.y));
      if (edge) get().dismantlePipeAt(edge.id);
    },

    beginMoveNode: (nodeId) => {
      const { snapshot } = get();
      if (!canBuildNow(snapshot)) return;
      set({
        pendingMove: { kind: "node", nodeId, soldBackEdgeIds: pipesSoldBackByMove(snapshot, nodeId) },
        pendingInstall: null,
        pendingPipe: null,
        rejection: null,
      });
    },

    beginMovePipe: (edgeId) => {
      if (!canBuildNow(get().snapshot)) return;
      set({
        pendingMove: { kind: "pipe", edgeId, fromNodeId: null },
        pendingInstall: null,
        pendingPipe: null,
        rejection: null,
      });
    },

    cancelMove: () => set({ pendingMove: null }),

    moveNodeTo: (at, rotation) => {
      const pending = get().pendingMove;
      if (pending?.kind !== "node") return;
      commit(() =>
        driver.applyCommand({
          type: "moveHardware",
          nodeId: pending.nodeId,
          at,
          ...(rotation === undefined ? {} : { rotation }),
        }),
      );
      // Cleared landed or refused, like pendingInstall: the refusal is a toast.
      set({ pendingMove: null });
    },

    movePipeTo: (fromNodeId, toNodeId) => {
      const pending = get().pendingMove;
      if (pending?.kind !== "pipe") return;
      commit(() =>
        driver.applyCommand({ type: "moveHardware", edgeId: pending.edgeId, fromNodeId, toNodeId }),
      );
      set({ pendingMove: null });
    },

    cleanTileAt: (at) => commit(() => driver.applyCommand({ type: "cleanTile", at })),

    rebindRecipeAt: (nodeId, recipeIds) =>
      commit(() => driver.applyCommand({ type: "bindRecipe", nodeId, recipeIds })),

    leaveSetup: () => {
      // Spec 0027: leaving setup with a sourced inputTank still unchosen is
      // refused (`sourcingUnchosen`) — check the first step's result before
      // taking the second, so a refusal surfaces as a rejection toast rather
      // than being silently swallowed, and the second `advancePhase` never
      // fires on top of a refusal that already left the engine in `setup`.
      const setupToDraw = driver.applyCommand({ type: "advancePhase" }); // setup -> draw
      if (!setupToDraw.ok) {
        set({ snapshot: driver.getSnapshot(), rejection: setupToDraw.rejection.message, installedUnits });
        return;
      }
      driver.applyCommand({ type: "advancePhase" }); // draw -> play
      set({ snapshot: driver.getSnapshot(), rejection: null, installedUnits });
    },

    advanceFromStatus: () => {
      driver.applyCommand({ type: "advancePhase" }); // status -> draw
      driver.applyCommand({ type: "advancePhase" }); // draw -> play
      set({ snapshot: driver.getSnapshot() });
    },

    clickTile: (at) => {
      const { pendingPipe, pendingInstall, pendingMove, snapshot } = get();

      if (pendingMove) {
        if (pendingMove.kind === "node") {
          get().moveNodeTo(at);
          return;
        }
        const node = nodeAt(snapshot, at);
        if (!node) {
          set({ rejection: "a pipe has to join two machines" });
          return;
        }
        if (pendingMove.fromNodeId === null) {
          set({ pendingMove: { ...pendingMove, fromNodeId: node.id }, rejection: null });
          return;
        }
        get().movePipeTo(pendingMove.fromNodeId, node.id);
        return;
      }

      // Play under a liveBuild rule shares the install flow, but a bare click
      // there must not dismantle: that stays an explicit inspector action.
      if (snapshot.phase === "setup" || (pendingInstall && canBuildNow(snapshot))) {
        if (!pendingInstall) {
          get().dismantleAt(at);
          return;
        }
        if (!CARDS[pendingInstall.cardId]?.deploysPipe) {
          get().installHardwareAt(pendingInstall.cardId, at, pendingInstall.recipeIds);
          return;
        }
        // A bought pipe, like a played one, runs machine to machine.
        const node = nodeAt(snapshot, at);
        if (!node) {
          set({ rejection: "a pipe has to join two machines" });
          return;
        }
        if (pendingInstall.fromNodeId === null) {
          set({ pendingInstall: { ...pendingInstall, fromNodeId: node.id }, rejection: null });
          return;
        }
        const { cardId, fromNodeId } = pendingInstall;
        set({ pendingInstall: null });
        commit(() =>
          driver.applyCommand({ type: "installHardware", cardId, at: null, fromNodeId, toNodeId: node.id }),
        );
        return;
      }

      if (!pendingPipe) return;
      const node = nodeAt(snapshot, at);
      if (!node) {
        set({ rejection: "a pipe has to end on a machine" });
        return;
      }
      get().completePipe(node.id);
    },

    startRun: () => {
      commit(() => driver.applyCommand({ type: "advancePhase" })); // play -> run
      set({ speed: "normal", pendingPipe: null, pendingInstall: null, pendingMove: null });
    },

    tick: () => {
      const snapshot = get().snapshot;
      if (snapshot.phase !== "run") return [];

      const events = driver.step();
      let next = driver.getSnapshot();

      if (next.isRunComplete || next.isRunFailed) {
        set({ snapshot: next, speed: "paused" });
        return events;
      }

      // The turn's ticks are spent. Stop in Status rather than collapsing
      // straight through to the next Play — the player needs to be able to
      // see the turn's outcome (criterion 9); advanceFromStatus() takes it
      // from here once they have.
      if (next.tickInTurn >= next.ticksPerTurn) {
        driver.applyCommand({ type: "advancePhase" }); // run -> status
        next = driver.getSnapshot();
        set({ snapshot: next, speed: "paused" });
        return events;
      }

      set({ snapshot: next });
      return events;
    },

    setSpeed: (speed) => set({ speed }),

    reject: (reason) => set({ rejection: reason }),
    clearRejection: () => set({ rejection: null }),

    fireDebugEvent: (event) => {
      for (const fn of [...listeners]) fn(event);
    },

    reset: () => {
      // Same reasoning as rebuild(): a restarted stage has nothing installed,
      // and driver.reset() rebuilds state directly rather than replaying
      // commands, so no hardware.dismantled events arrive to clear this.
      installedUnits = {};
      driver.reset();
      enterPlay();
      set(freshBoard());
    },
  };
});

/**
 * What each kind of neglect is called, in the player's terms. Exhaustive over
 * `PollutionReason` (`protocol/events.ts`) so a tenth reason fails the build
 * here rather than silently falling back to a generic line.
 */
const POLLUTION_NOTICE: Record<PollutionReason, string> = {
  leak: "a line was left to corrode through",
  overheat: "a machine was left cooking",
  sludge: "material was left standing until it set",
  catalystDead: "a catalyst bed was poisoned to nothing",
  spill: "a spill was left to spread across the tile",
  vermin: "stagnant sludge was left to draw vermin",
  powerOutage: "the grid was left to surge and drop",
  structuralShear: "the rig was left unbraced for a heatwave or tremor",
  burst: "a line was left to run over pressure until it burst",
};

/**
 * Deck pollution has no board position, so it arrives as a notice rather than
 * an in-world effect. A hazard card appearing in a later hand with no
 * explanation reads as the game cheating — naming the offending unit
 * (`sourceId`) is what makes it read as a consequence rather than an ambush
 * (`02-cards-and-economy.md` §4.1, "Explicit Neglect & Incident Attribution").
 */
subscribeEvents((event) => {
  if (event.type !== "deck.polluted") return;
  const name = CARDS[event.cardId]?.name ?? event.cardId;
  const why = POLLUTION_NOTICE[event.reason];
  useGameStore.setState({
    notice: `${why} (${event.sourceId}) — ${name} shuffled into your deck`,
  });
});
